import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { Button } from '@hotelplan/components.common.buttons';
import { WhiteBoxModal } from '@hotelplan/components.common.modals';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import type { IConfirmationProps } from 'components/domain/confirmation/useConfirmation';

export interface IConfirmationModalProps
  extends IConfirmationProps,
    React.PropsWithChildren<{}> {
  title?: string;
  name?: string;
  question?: string;
}

const ConfirmationModalWrapper = styled(WhiteBoxModal)(
  ({ theme: { FONT_SIZE } }) =>
    sx2CssThemeFn({
      '.modal-header': {
        py: [2, '14px'],
        px: [3, 4],
        h2: FONT_SIZE.S,
      },
      '.modal-wrapper': {
        maxWidth: [null, '500px'],
      },
      '.modal-body': {
        px: [3, 4],
        pt: [4, '30px'],
        pb: ['75px', '40px'],
      },
      '.confirmation-yes': {
        paddingLeft: 3,
        paddingRight: 3,
      },
      '.confirmation-no': {
        ...FONT_SIZE.BUTTON,
        paddingTop: 2,
        paddingBottom: 2,
      },
    })
);

const ConfirmationControl = styled.div(
  {
    display: 'flex',
  },
  ({ theme }) => theme.modals.confirmationControls
);

const ConfirmationModalText = styled.p(({ theme: { FONT_SIZE } }) =>
  sx2CssThemeFn(FONT_SIZE.LARGE)
);

export const ConfirmationModal: React.FC<IConfirmationModalProps> = props => {
  const [t] = useTranslation('common');
  const {
    isShow,
    children,
    onApply,
    onCancel,
    title,
    question = t('common:confirmation.question'),
    ...otherProps
  } = props;

  return (
    <ConfirmationModalWrapper
      name="confirmation"
      show={isShow}
      title={title}
      onClose={onCancel}
      controls={
        <ConfirmationControl>
          <Button
            variant="primary"
            className="confirmation-yes"
            onClick={onApply}
          >
            {t('common:confirmation.yes')}
          </Button>
          <Button
            variant="linkBtn"
            className="confirmation-no"
            onClick={onCancel}
          >
            {t('common:confirmation.cancel')}
          </Button>
        </ConfirmationControl>
      }
      {...otherProps}
    >
      <ConfirmationModalText>{question}</ConfirmationModalText>
      {children}
    </ConfirmationModalWrapper>
  );
};
